import { Badge } from "@/components/ui/badge";
import { ChevronDown } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface TicketStatusBadgeProps {
  status: string;
  color?: string | null;
  withChevron?: boolean;
}

export function TicketStatusBadge({ status, color, withChevron }: TicketStatusBadgeProps) {
  const { t } = useTranslation("home_tickets_ticket");
  const compactClassName = "h-5 px-2 text-[10px] leading-none font-semibold gap-1";
  const chevron = withChevron ? <ChevronDown className="h-3 w-3 shrink-0 opacity-60" /> : null;

  let className = compactClassName;
  let style: CSSProperties | undefined;
  let label: ReactNode = status;

  if (color) {
    style = {
      borderColor: color,
      backgroundColor: `color-mix(in srgb, ${color} 10%, transparent)`,
      color,
    };
  } else {
    switch (status) {
      case "created":
        className +=
          " border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900 dark:text-blue-100";
        label = t("status.created");
        break;
      case "in_process":
        className +=
          " border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900 dark:text-amber-100";
        label = t("status.in_process");
        break;
      case "completed":
        className +=
          " border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900 dark:text-green-100";
        label = t("status.completed");
        break;
      case "canceled":
        className +=
          " border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900 dark:text-red-100";
        label = t("status.canceled");
        break;
      default:
        break;
    }
  }

  return (
    <Badge variant="outline" className={className} style={style}>
      {label}
      {chevron}
    </Badge>
  );
}
